/**
 * Generic reference-switchboard grid (TableVirtuoso + column filter + sort +
 * resize + pagination + optional footer totals). Presentational: the column
 * visibility/order controller and the width controller are INJECTED so each
 * function keeps its own persistence strategy (Procurement=localStorage,
 * SaleControl=server prefs) without conditional hooks here.
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type ComponentProps, type MouseEvent, type ReactNode, useEffect, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import PageDataState from '@/components/PageDataState'
import ResizeHandle from '@/components/ResizeHandle'
import {
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  DataframeBody,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import {
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
  tableInnerHeight,
} from '@/theme/responsive'
import { titleCaseName } from '@/utils/text'
import type { UseDatasheetColumnsMenuResult } from '@/utils/datasheetColumns/types'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import { filterReferenceRows, formatReferenceValue } from './referenceFormat'
import type { ReferenceMenuColumn, ReferencePageConfig, TotalsMode } from './types'

type MuiTableProps = ComponentProps<typeof Table>
type MuiTableHeadProps = ComponentProps<typeof TableHead>
type MuiTableBodyProps = ComponentProps<typeof TableBody>
type MuiTableRowProps = ComponentProps<typeof TableRow>

export interface ReferenceWidthController {
  getWidth: (field: string, fallback?: number) => number | undefined
  startResize: (field: string, e: MouseEvent<HTMLElement>) => void
}

export interface ReferenceGridBaseProps {
  page: ReferencePageConfig
  rows: Record<string, unknown>[]
  search: string
  onSearchChange: (value: string) => void
  loading?: boolean
  fetching?: boolean
  error?: unknown
  onRetry?: () => void
  /** Fill parent height (drop 620 cap) — only when parent is bounded. */
  fillHeight?: boolean
  /** Injected column visibility/order controller (local or server prefs). */
  columnsCtrl: UseDatasheetColumnsMenuResult<ReferenceMenuColumn>
  /** Injected width controller (per-function storage key). */
  widths: ReferenceWidthController
  totalsMode: TotalsMode
  /**
   * Optional leading content in the FilterBar (before Search/Columns) — used by
   * embeds (vd Factory Data hub "Item List") to prepend a Back + accent pill.
   */
  headerSlot?: ReactNode
  /**
   * When true, cell/header width falls back to the config `width` if the user
   * hasn't resized (SaleControl behavior). When false, width applies only after
   * an explicit resize (Procurement behavior). Kept to avoid changing sizing.
   */
  fallbackToConfigWidth?: boolean
}

export default function ReferenceGridBase({
  page,
  rows,
  search,
  onSearchChange,
  loading = false,
  fetching = false,
  error,
  onRetry,
  fillHeight = false,
  columnsCtrl,
  widths,
  totalsMode,
  headerSlot,
  fallbackToConfigWidth = false,
}: ReferenceGridBaseProps) {
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = widths
  const widthOf = (field: string, configWidth?: number) =>
    fallbackToConfigWidth ? getWidth(field) ?? configWidth : getWidth(field)

  const [pageIndex, setPageIndex] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ field: string; position: ColumnFilterAnchorPosition } | null>(null)

  // Reset per-column filters when switching page (different schema).
  useEffect(() => {
    columnFilters.clearAll()
  }, [page.pageKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = useMemo(
    () =>
      filterReferenceRows(rows, search, page.columns)
        .filter((row) => rowMatchesAllColumnFilters(row, columnFilters.filters))
        .map((row, index) => ({ id: `${page.pageKey}-${index}`, ...row }) as Record<string, unknown>),
    [page.pageKey, page.columns, rows, search, columnFilters.filters],
  )

  const popoverColumn = useMemo(
    () => (colPopover ? page.columns.find((c) => c.field === colPopover.field) ?? null : null),
    [colPopover, page.columns],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () =>
      popoverColumn
        ? inferColumnFilterKind({ key: popoverColumn.field, kind: popoverColumn.kind } as never, rows)
        : 'text',
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(rows, colPopover.field) : undefined),
    [popoverKind, colPopover, rows],
  )

  useEffect(() => {
    setPageIndex(0)
  }, [page.pageKey, search])

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, page.columns.map((c) => ({ field: c.field, kind: c.kind }))),
    [filteredRows, page.columns, sortState],
  )

  const visibleRows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = pageIndex * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [pageIndex, rowsPerPage, sortedRows])

  const isEmpty = !loading && !error && filteredRows.length === 0

  // Footer totals — two conventions preserved:
  //  - sum-flag: only columns flagged `sum` (over ALL page columns → footer shows
  //    even if the summed column is hidden). Footer hidden if no sum columns.
  //  - all-numeric-except-noTotal: every visible numeric column minus `noTotal`;
  //    footer always shown.
  const sumFields = useMemo(() => {
    if (totalsMode === 'sum-flag') {
      return new Set(page.columns.filter((c) => c.sum).map((c) => c.field))
    }
    return new Set(visibleColumns.filter((c) => c.kind === 'number' && !c.noTotal).map((c) => c.field))
  }, [totalsMode, page.columns, visibleColumns])
  const showFooter = totalsMode === 'all-numeric-except-noTotal' || sumFields.size > 0
  const footerTotals = useMemo(() => {
    const totals = new Map<string, number | null>()
    for (const field of sumFields) {
      let total = 0
      let count = 0
      for (const row of filteredRows) {
        const value = row[field]
        const n = typeof value === 'number' ? value : Number(value)
        if (!Number.isFinite(n)) continue
        total += n
        count += 1
      }
      totals.set(field, totalsMode === 'sum-flag' ? total : count > 0 ? total : null)
    }
    return totals
  }, [filteredRows, sumFields, totalsMode])

  const minTableWidth = useMemo(
    () => Math.max(1200, visibleColumns.reduce((total, c) => total + (c.width ?? c.minWidth ?? 160), 0)),
    [visibleColumns],
  )
  const gridKey = `${page.pageKey}:${rowsPerPage}:${pageIndex}:${filteredRows.length}:${visibleColumns.map((c) => c.field).join(',')}`
  const hasActiveFilters = !!search.trim() || columnFilters.hasAny
  const activeFilterCount = (search.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    onSearchChange('')
    columnFilters.clearAll()
    setRowsPerPage(-1)
    setPageIndex(0)
    filterPopover.close()
  }
  const toggleSort = (field: string) => {
    setSortState((current) => toggleTableSort(current, field))
    setPageIndex(0)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minHeight: 0,
        flex: 1,
        ...(fillHeight ? { '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' } } : {}),
      }}
    >
      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            gap: { xs: 0.5, sm: 1 },
            flexWrap: 'wrap',
            minHeight: { xs: 24, sm: 30 },
            py: 0.125,
          }}
        >
          <FilterBar
            sx={{ px: { xs: 0.25, sm: 0.5 }, pt: 0.75 }}
            hasActiveFilters={hasActiveFilters}
            onClearAll={clearAllFilters}
            activeFilterCount={activeFilterCount}
          >
            {headerSlot}
            <FilterButton label="Search" active={!!search.trim()} onClick={(e) => filterPopover.open('search', e)} />
            <Button
              size="small"
              variant="outlined"
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => openColMenu(e.currentTarget)}
              sx={filterBarBtnSx}
              title="Columns"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
            </Button>
          </FilterBar>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(loading || fetching) && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Loaded: {rows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : pageIndex}
              onPageChange={(_, newPage) => setPageIndex(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPageIndex(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={filterPopover.close}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={search}
              onChange={(v) => {
                onSearchChange(v)
                setPageIndex(0)
              }}
              placeholder="Filter loaded rows..."
              label="Search reference"
              onClear={filterPopover.close}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.headerName ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.field] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.field, v)
            setPageIndex(0)
          }}
          options={popoverOptions}
        />

        <PageDataState
          loading={loading}
          error={error}
          empty={isEmpty}
          emptyMessage="No rows (or empty table)."
          onRetry={onRetry}
          minHeight={300}
        >
          <DataframeBody
            loading={loading}
            fetching={fetching}
            // fillHeight: minHeight:0 để flex child CO được khi viewport thấp —
            // tránh ép bảng ≥320 gây tràn → scrollbar bật/tắt → ResizeObserver loop
            // (giật giật khi giảm height, vd References → List and Valo). Parent
            // bounded vẫn cấp height qua flex:1 nên bảng fill khi đủ cao.
            sx={fillHeight ? { flex: 1, minHeight: 0, overflow: 'hidden' } : tableInnerHeight(620)}
          >
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={visibleRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((column) => {
                    const w = widthOf(column.field, column.width)
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(column.field)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', position: 'relative' }}
                      >
                        {column.headerName}
                        {getTableSortSuffix(sortState, column.field)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(column.field)}
                          onClick={(position) => setColPopover({ field: column.field, position })}
                          ariaLabel={`Filter ${column.headerName}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(column.field, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={
                showFooter
                  ? () => (
                      <TableRow>
                        {visibleColumns.map((column, index) => {
                          const w = widthOf(column.field, column.width)
                          const total = sumFields.has(column.field) ? footerTotals.get(column.field) : null
                          const text =
                            total == null ? '' : formatReferenceValue(total, 'number', column.precision ?? 0)
                          return (
                            <TableCell
                              key={column.field}
                              align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                              style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                              sx={{
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                py: 0.5,
                                bgcolor: 'background.paper',
                                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {sumFields.has(column.field) ? text : index === 0 ? 'Total' : ''}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  : undefined
              }
              itemContent={(_: number, row: Record<string, unknown>) =>
                visibleColumns.map((column) => {
                  const value = column.proper
                    ? titleCaseName(row[column.field])
                    : formatReferenceValue(row[column.field], column.kind, column.precision ?? 0)
                  const userW = getWidth(column.field)
                  const w = widthOf(column.field, column.width)
                  // convert-table §7.8: wrap when column flags `wrap` OR user resized.
                  const wrap = column.wrap || userW != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {value || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={column.field}
                      align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={value || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {value || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: MuiTableProps) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: (props: MuiTableHeadProps) => <TableHead {...props} component="thead" />,
                TableBody: (props: MuiTableBodyProps) => <TableBody {...props} component="tbody" />,
                TableRow: (props: MuiTableRowProps) => <TableRow {...props} hover />,
              }}
            />
          </DataframeBody>
        </PageDataState>
      </Box>
      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
